// MetaTable.js
// Simple meta table: left column = label, right column = text
// Renders **horizontal borders only**. Uses Fluent UI v8 components for typography/layout.
// Props:
// - items: Array<{ label: string, text: React.ReactNode }>
// - labelColWidth?: string (e.g., '220px')
// - className?: string
// - dense?: boolean (smaller paddings)
// - ariaLabel?: string

import React from 'react';
import { Stack, Text } from '@fluentui/react';
import { mergeStyleSets } from '@fluentui/react';

export default function MetaTable({ borderless=false, items = [], labelColWidth = '220px', className, dense = false, ariaLabel = 'metadata table' }) {
  const styles = React.useMemo(() => mergeStyleSets({
    table: {
      width: '100%',
      borderCollapse: 'collapse',
    },
    colgroup: {},
    // Apply horizontal rules between rows only:
    tbody: {
      selectors: {
        'tr + tr td': {
          borderTop: borderless ? 'none' : '1px solid var(--neutralLight, #e1e1e1)',
        },
        'tr:first-child td': {
          borderTop: borderless ? 'none' : '1px solid var(--neutralLight, #e1e1e1)',
        },
        'tr:last-child td': {
          borderBottom: borderless ? 'none' : '1px solid var(--neutralLight, #e1e1e1)',
        },
      },
    },
    row: {},
    cellLabel: {
      width: labelColWidth,
      verticalAlign: 'top',
      padding: dense ? '6px 10px' : '10px 12px',
    },
    cellText: {
      verticalAlign: 'top',
      padding: dense ? '6px 12px' : '10px 14px',
    },
    zebra: {
      selectors: {
        'tr:nth-child(odd) td': {
          background: 'rgba(0,0,0,0.015)',
        },
      },
    },
    labelText: { fontWeight: 600 },
    emptyNote: { opacity: 0.7 },
  }), [labelColWidth, dense, borderless]);

  if (!items || items.length === 0) {
    return (
      <Stack className={className}>
        <Text variant="small" className={styles.emptyNote}>No data</Text>
      </Stack>
    );
  }

  return (
    // <Stack className={className}>
      <table className={styles.table} aria-label={ariaLabel}>
        {/* <colgroup>
          <col style={{ width: labelColWidth }} />
          <col />
        </colgroup> */}
        <tbody className={`${styles.tbody}`}>
          {items.map((it, idx) => (
            <tr key={idx} className={styles.row}>
              <td className={styles.cellLabel}>
                <Text className={styles.labelText} variant={dense ? 'small' : 'medium'}>{it.label}</Text>
              </td>
              <td className={styles.cellText}>
                {typeof it.text === 'string' ? (
                  <Text variant={dense ? 'small' : 'medium'}>{it.onRender ? it.onRender(it) : it.text}</Text>
                ) : (
                  it.text
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    // </Stack>
  );
}

// ---- Example usage ----
// const meta = [
//   { label: 'Client', text: 'BC Hydro' },
//   { label: 'Status', text: 'Open' },
//   { label: 'PM', text: <a href="#">Sally Rudd</a> },
//   { label: 'Dates', text: 'Jun 2021 – Mar 2023' },
// ];
// <MetaTable items={meta} labelColWidth="240px" />
